// Экран интро: имя игры, сама машина и строка о ней, см. GDD, раздел 7.3.
//
// Машина стоит здесь в покое: створки закрыты, стрелки манометров на нуле. Это то же поле,
// на которое игрок попадёт после нажатия, только целиком — с трубой и тележкой, которые
// в игре уходят за края экрана. Собирает её ui/machine.ts, оттуда же её берёт игровое поле.
//
// Фон игровой: путь на нём уходит в пустой каньон, и машина встаёт прямо на него. Картинку
// из папки splash игра не берёт — на ней нарисован свой паровоз, и он спорил бы с этим.
//
// Имя игры набрано шрифтом, как на референсе GDD. Скелет логотипа тут не встаёт: он рисует
// тот же паровоз ещё раз, только мельче.
//
// Нажатие ловит весь экран, а не кнопка. Клик здесь нужен браузеру: без жеста игрока
// звук не запускается, поэтому промахнуться мимо нажатия игрок не должен.
//
// Строка об игре растёт вверх от своего низа: длинный перевод уходит в пустую середину
// экрана, а не наползает на приглашение под ним.

import { Container, Text, type TextStyleOptions } from 'pixi.js';

import { TITLE_FONT } from '../assets/catalog';
import type { AssetManager } from '../assets/manager';
import { text, textFamily } from '../i18n/text';
import { Background } from '../ui/background';
import { isMobileLayout } from '../ui/layout';
import { INTRO_VIEW, stillMachine } from '../ui/machine';
import type { Scene, Viewport } from '../ui/scene';

/**
 * Имя игры.
 *
 * Перевода у него нет и не будет: имя игры — это имя, а не надпись. Поэтому оно не идёт
 * через пак перевода, в отличие от всего остального текста экрана.
 */
const GAME_NAME: string = 'STEAM SPIN';

// Координаты внутри макета отсчитываются от центра экрана.

/** Высота имени игры. Оно стоит вверху экрана, как на референсе GDD. */
const NAME_Y: number = -460;

/** Низ строки об игре и ширина, по которой она переносится. */
const TAGLINE_BOTTOM: number = 380;
const TAGLINE_WIDTH: number = 1500;
const MOBILE_TAGLINE_WIDTH: number = 0.85;

/** Высота приглашения. Оно стоит под строкой об игре. */
const PROMPT_Y: number = 465;

// Высота кегля надписей.
const NAME_SIZE: number = 100;
const TAGLINE_SIZE: number = 40;
const PROMPT_SIZE: number = 46;

/** Сколько длится один вздох приглашения. */
const PULSE_SECONDS: number = 1.6;

/** До какой прозрачности приглашение гаснет на вздохе. */
const PULSE_LOW: number = 0.45;

/** Стиль надписи: светлые буквы с тёмной обводкой, иначе на картинке фона их не прочитать. */
function introStyle(family: string, size: number): TextStyleOptions {
  return {
    fontFamily: textFamily(family),
    fontSize: size,
    fontWeight: 'bold',
    fill: 0xfff4dd,
    stroke: { color: 0x40210f, width: size / 9, join: 'round' },
    align: 'center',
    letterSpacing: 2,
  };
}

/** Экран интро с именем игры, машиной и строкой об игре. */
export class IntroScene implements Scene {
  readonly view: Container = new Container();

  readonly #background: Background;
  readonly #machine: Container;
  readonly #name: Text;
  readonly #tagline: Text;
  readonly #prompt: Text;
  readonly #tapped: Promise<void>;

  #pulse: number = 0;

  constructor(assets: AssetManager) {
    const family: string = assets.font(TITLE_FONT);

    this.#background = new Background(assets);

    // Фон закрывает экран целиком, поэтому клик по нему — это клик по любому месту экрана.
    this.#background.view.eventMode = 'static';
    this.#background.view.cursor = 'pointer';
    this.#tapped = new Promise<void>((resolve) => {
      this.#background.view.once('pointerdown', () => {
        resolve();
      });
    });

    // Машина стоит вдали: с этой же позы игровое поле начинает её подъезд, см. ui/arrival.ts.
    const machine: Container = stillMachine(assets, INTRO_VIEW.height);
    machine.y = INTRO_VIEW.centerY;
    this.#machine = machine;

    const name = new Text({ text: GAME_NAME, style: introStyle(family, NAME_SIZE) });
    name.anchor.set(0.5);
    name.y = NAME_Y;
    this.#name = name;

    const tagline = new Text({
      text: text('introTagline'),
      // Слова рвутся: тайский и китайский пишут без пробелов, и вся строка идёт на них одним
      // словом, которое перенос по пробелам не тронул бы, см. ui/popup.ts.
      style: { ...introStyle(family, TAGLINE_SIZE), wordWrap: true, wordWrapWidth: TAGLINE_WIDTH, breakWords: true, lineHeight: TAGLINE_SIZE * 1.35 },
    });
    tagline.anchor.set(0.5, 1);
    tagline.y = TAGLINE_BOTTOM;
    this.#tagline = tagline;

    this.#prompt = new Text({ text: text('tapToStart'), style: introStyle(family, PROMPT_SIZE) });
    this.#prompt.anchor.set(0.5);
    this.#prompt.y = PROMPT_Y;

    this.view.addChild(this.#background.view, machine, name, tagline, this.#prompt);
  }

  /** Ждёт первый клик по экрану. */
  tapped(): Promise<void> {
    return this.#tapped;
  }

  /** Качает прозрачность приглашения: неподвижную надпись игрок принимает за часть картинки. */
  update(seconds: number): void {
    this.#background.update(seconds);

    this.#pulse = (this.#pulse + seconds) % PULSE_SECONDS;

    const wave: number = 0.5 + 0.5 * Math.cos((2 * Math.PI * this.#pulse) / PULSE_SECONDS);
    this.#prompt.alpha = PULSE_LOW + (1 - PULSE_LOW) * wave;
  }

  layout(viewport: Viewport): void {
    this.#background.layout(viewport);

    // На телефоне корневой масштаб диктует ширина и сделал бы текст в два-три раза
    // мельче читаемого. Увеличиваем только содержимое интро: фон по-прежнему заполняет экран.
    const mobile: boolean = isMobileLayout(viewport);
    const scale: number = mobile ? Math.min(1.8, Math.max(1, 18 / (TAGLINE_SIZE * viewport.scale))) : 1;
    // Увеличение текста не должно увеличивать занятую им ширину: иначе строка стала бы
    // читаемой, но ушла бы за края телефона. Width задан до масштаба узла.
    this.#tagline.style.wordWrapWidth = mobile ? Math.min(TAGLINE_WIDTH, viewport.width * MOBILE_TAGLINE_WIDTH / scale) : TAGLINE_WIDTH;
    const y: number = INTRO_VIEW.centerY * (1 - scale);

    for (const [view, baseY] of [[this.#machine, INTRO_VIEW.centerY], [this.#name, NAME_Y], [this.#tagline, TAGLINE_BOTTOM], [this.#prompt, PROMPT_Y]] as const) {
      view.scale.set(scale);
      view.y = baseY * scale + y;
    }
  }

  destroy(): void {
    this.view.destroy({ children: true });
  }
}
